import { DEEPLINK_DEFINITIONS, getDeeplinkRoutePrefix } from './bootstrapDeepLinkDefinitions';
import { changeRoute } from '../actions/publicActions';
import DeepLinkModule from '../components/DeepLinkModule';
import { DeeplinkRoute, ROUTE_TO_VIEW_DEFINITIONS } from '../store/schema/DeeplinkRoute';
import type { DeeplinkView } from '../store/schema/DeeplinkView';
import getStore from '../store/store';
import { macIconRegistration } from 'mac-icons';
import { getMailAriaTenantTokens, logUsage } from 'owa-analytics';
import { calendarIconsRegistration } from 'owa-calendar-icons';
import { stashCalendarFoldersFromSessionData } from 'owa-calendarsapi-outlook/lib/services/calendarFoldersFromSessionData';
import { getOpxHostApp, MAC_OUTLOOK } from 'owa-config';
import { controlIconRegistration } from 'owa-control-icons';
import { mailIconsRegistration } from 'owa-mail-icons';
import { getOpxHostApi } from 'owa-opx';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import type { RegisterRouteFunction, RouterOptions } from 'owa-router';
import type { SessionData } from 'owa-service/lib/types/SessionData';
import { OwaWorkload } from 'owa-workloads';
import * as React from 'react';
import type { BootStrategies } from 'owa-shared-start';
import { Module } from 'owa-bootstrap';
import type { AppBootstrapOptions } from 'owa-app-module/lib/types/AppBootstrapOptions';
import { getHostedScenarioByUrl } from 'owa-opx-routes';

export function getCalendarDeepLinkBootstrapOptions(
    sessionPromise?: Promise<SessionData | undefined>,
    strategies?: BootStrategies
): Promise<AppBootstrapOptions> {
    sessionPromise = sessionPromise.then(stashCalendarFoldersFromSessionData);

    const deeplinkView = determineDeeplinkView();
    if (!deeplinkView) {
        return Promise.reject(new Error('NoCalendarDeeplinkView'));
    }

    const deeplinkDefinition = DEEPLINK_DEFINITIONS[deeplinkView];

    const calendarRouterOptions: RouterOptions = {
        routeGenerator: Promise.resolve(getCurrentDeepLinkRoute),
        vdir: 'calendar',
        defaultRoute: '/',
        staticUrl: isHostAppFeatureEnabled('routeOptionsStaticUrl'), // don't update the address bar in OPXs
        registerRoutes: initializeDeepLinkCalendarRoutes,
    };

    return Promise.resolve({
        initializeState: () => {},
        sessionPromise,
        strategies,
        routerOptions: calendarRouterOptions,
        workload: OwaWorkload.Calendar,
        analyticsOptions: {
            ariaTenantTokens: getMailAriaTenantTokens(),
        },
        iconFonts: [
            ...(getOpxHostApp() === MAC_OUTLOOK ? [macIconRegistration] : []),
            controlIconRegistration,
            calendarIconsRegistration,
            mailIconsRegistration,
        ],
        getModuleName: deeplinkDefinition.getModuleName,
        postLazyAction: deeplinkDefinition.postLazyAction,
        appModuleProps: {
            module: Module.CalendarDeepLink,
            renderModule: () => <DeepLinkModule />,
        },
    });
}

function initializeDeepLinkCalendarRoutes(registerRoute: RegisterRouteFunction) {
    Object.keys(DEEPLINK_DEFINITIONS).forEach(view => {
        const definition = DEEPLINK_DEFINITIONS[view];
        definition.getRouteRegistrations().forEach(route => {
            registerRoute(route, definition.routeHandler);
        });
    });

    if (isHostAppFeatureEnabled('opxDeeplink')) {
        const opxHostApi = getOpxHostApi();
        if (opxHostApi.registerNewRouteHandler) {
            opxHostApi.registerNewRouteHandler(changeRoute);
        }
    }

    return Promise.resolve();
}

function getCurrentDeepLinkRoute() {
    const { displayedView } = getStore();
    const deepLinkDefinition = DEEPLINK_DEFINITIONS[displayedView];

    if (!deepLinkDefinition) {
        // Instrumenting to track an intermittent
        // failure while launching deeplinks.
        logUsage('InvalidDeeplinkView', [displayedView]);
    }

    return deepLinkDefinition.getRouteSegments();
}

/**
 * Determines if path matches a given route `/(opx)deeplink/${deeplinkView}` and returns the associated view
 */
function determineDeeplinkView(): DeeplinkView | null {
    const deeplinkPrefix = getDeeplinkRoutePrefix();
    if (deeplinkPrefix === 'deeplink') {
        const path = window.location ? window.location.pathname : '';
        const segments = path.split('/');
        const deeplinkSegment = segments.indexOf(deeplinkPrefix);
        if (deeplinkSegment >= 0 && segments.length > deeplinkSegment + 1) {
            const route = segments[deeplinkSegment + 1] as DeeplinkRoute;
            return ROUTE_TO_VIEW_DEFINITIONS[route];
        }
        return null;
    } else {
        return getHostedScenarioByUrl() as DeeplinkView;
    }
}
